import { useEffect, useRef, useState } from 'react'
import { ExternalLink, Globe, RefreshCw } from 'lucide-react'
import { Skeleton, Spinner } from '@/components/ui'
import { cn } from '@/utils'
import { useRagProxyBase } from '@/hooks/useRagProxyBase'
import { cleanPassageText } from './cleanPassageText'
import { isPdfSource } from './isPdfSource'
import { getMediaTypeStyle } from './mediaTypeStyles'
import type { DisplaySource } from '@/types/source'

/** Passage quote-card: muted surface with the media-type accent edge. */
export function PassageCard({
  source,
  children,
  className,
}: {
  source: DisplaySource
  children: React.ReactNode
  className?: string
}) {
  const style = getMediaTypeStyle(source.mediaType)
  return (
    <div className={cn('bg-muted rounded-lg p-5 border-l-2', style.border, className)}>
      {children}
    </div>
  )
}

/** Placeholder when media can't be previewed (expired SAS, fetch failure). */
export function UnavailablePreview({
  source,
  message,
  onRefresh,
  isRefreshing,
}: {
  source: DisplaySource
  message: string
  onRefresh?: () => void
  isRefreshing?: boolean
}) {
  const style = getMediaTypeStyle(source.mediaType)
  return (
    <div className="bg-muted rounded-lg px-6 py-8 flex flex-col items-center text-center gap-3">
      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', style.iconBg)}>
        <style.Icon className={cn('w-5 h-5', style.iconText)} />
      </div>
      <div className="text-sm text-muted-foreground">{message}</div>
      <RefreshPreviewButton onRefresh={onRefresh} isRefreshing={isRefreshing} />
    </div>
  )
}

export function DocumentViewer({
  source,
  embedUrl,
  embedStatus,
}: {
  source: DisplaySource
  embedUrl: string | null
  embedStatus: 'ready' | 'loading' | 'error'
}) {
  const isPdf = isPdfSource(source)

  if (isPdf && !source.isExpired && embedStatus === 'loading') {
    return <Skeleton className="h-full min-h-64 w-full rounded-lg" />
  }

  if (isPdf && !source.isExpired && embedUrl) {
    let src = embedUrl
    if (source.page_number && source.page_number > 0) {
      const separator = src.includes('#') ? '&' : '#'
      src = `${src}${separator}page=${source.page_number}`
    }
    return (
      <iframe
        src={src}
        title={source.title}
        className="w-full h-full min-h-64 rounded-lg border border-border"
      />
    )
  }

  return (
    <PassageCard source={source}>
      {source.formattedPageNumber && (
        <div className="text-xs text-muted-foreground mb-3 font-medium">
          {source.formattedPageNumber}
        </div>
      )}
      <div className="text-[15px] text-foreground leading-[1.7] whitespace-pre-wrap">
        {cleanPassageText(source.document)}
      </div>
    </PassageCard>
  )
}

export function RefreshPreviewButton({
  onRefresh,
  isRefreshing,
}: {
  onRefresh?: () => void
  isRefreshing?: boolean
}) {
  if (!onRefresh) return null
  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={isRefreshing}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-transparent text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
    >
      <RefreshCw className={cn('w-3.5 h-3.5', isRefreshing && 'animate-spin')} />
      {isRefreshing ? 'Refreshing…' : 'Refresh preview'}
    </button>
  )
}

export function VideoViewer({
  source,
  onRefresh,
  isRefreshing,
}: {
  source: DisplaySource
  onRefresh?: () => void
  isRefreshing?: boolean
}) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (videoRef.current && source.start_time != null) {
      videoRef.current.currentTime = source.start_time
    }
  }, [source.start_time])

  return (
    <div>
      {!source.isExpired ? (
        <video
          ref={videoRef}
          src={source.storage_url ?? undefined}
          controls
          className="w-full rounded-lg bg-black"
        >
          Your browser does not support the video element.
        </video>
      ) : (
        <UnavailablePreview
          source={source}
          message="Media preview unavailable — showing text excerpt"
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      )}
      {source.formattedTimeRange && (
        <div className="text-xs text-muted-foreground mt-2">
          Relevant segment: {source.formattedTimeRange.replace(' - ', ' \u2013 ')}
        </div>
      )}
      <PassageCard source={source} className="p-4 mt-3 text-sm leading-relaxed">
        {source.speaker && (
          <span className="font-medium text-violet-600 dark:text-violet-400">
            {source.speaker}:{' '}
          </span>
        )}
        <span className="italic">&ldquo;{cleanPassageText(source.document)}&rdquo;</span>
      </PassageCard>
    </div>
  )
}

export function AudioViewer({
  source,
  onRefresh,
  isRefreshing,
}: {
  source: DisplaySource
  onRefresh?: () => void
  isRefreshing?: boolean
}) {
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (audioRef.current && source.start_time != null) {
      audioRef.current.currentTime = source.start_time
    }
  }, [source.start_time])

  return (
    <div>
      {!source.isExpired ? (
        <audio
          ref={audioRef}
          src={source.storage_url ?? undefined}
          controls
          className="w-full mb-3"
        >
          Your browser does not support the audio element.
        </audio>
      ) : (
        <div className="mb-3">
          <UnavailablePreview
            source={source}
            message="Media preview unavailable — showing text excerpt"
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
          />
        </div>
      )}
      {source.formattedTimeRange && (
        <div className="text-xs text-muted-foreground mb-3">
          Relevant segment: {source.formattedTimeRange.replace(' - ', ' \u2013 ')}
        </div>
      )}
      <PassageCard source={source} className="p-4 text-sm leading-relaxed">
        {source.speaker && (
          <span className="font-medium text-orange-600 dark:text-orange-400">
            {source.speaker}:{' '}
          </span>
        )}
        <span className="italic">&ldquo;{cleanPassageText(source.document)}&rdquo;</span>
      </PassageCard>
    </div>
  )
}

function extractScormId(originalUrl: string | null | undefined): string | null {
  if (!originalUrl) return null
  const match = originalUrl.match(/\/scorm\/([^/]+)\/lesson\//)
  return match ? match[1] : null
}

export function ScormViewer({
  source,
  ragServerUrl,
  onEntryUrl,
}: {
  source: DisplaySource
  ragServerUrl?: string
  onEntryUrl?: (url: string | null) => void
}) {
  const [mode, setMode] = useState<'loading' | 'interactive' | 'fallback'>('loading')
  const [entryUrl, setEntryUrl] = useState<string | null>(null)
  const globalRagUrl = useRagProxyBase()

  useEffect(() => {
    const scormId = extractScormId(source.original_url)
    if (!scormId) {
      setMode('fallback')
      onEntryUrl?.(null)
      return
    }

    const ragBaseUrl = (ragServerUrl || globalRagUrl || '/rag-proxy').replace(/\/$/, '')
    const url = `${ragBaseUrl}/api/sources/scorm/${scormId}/viewer`

    fetch(url)
      .then((res) => {
        if (res.ok) {
          setEntryUrl(url)
          setMode('interactive')
          onEntryUrl?.(url)
        } else {
          setMode('fallback')
          onEntryUrl?.(null)
        }
      })
      .catch(() => {
        setMode('fallback')
        onEntryUrl?.(null)
      })
  }, [source.original_url, ragServerUrl, globalRagUrl, onEntryUrl])

  if (mode === 'loading') {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        <Spinner className="mr-2" />
        Loading course module...
      </div>
    )
  }

  if (mode === 'interactive' && entryUrl) {
    return (
      <div className="h-full">
        <iframe
          src={entryUrl}
          title={source.title}
          className="w-full h-full min-h-64 rounded-lg border border-border"
          sandbox="allow-scripts allow-same-origin allow-forms"
        />
      </div>
    )
  }

  return (
    <PassageCard source={source} className="text-sm text-foreground leading-[1.7] whitespace-pre-wrap">
      {cleanPassageText(source.document)}
    </PassageCard>
  )
}

export function WebViewer({ source }: { source: DisplaySource }) {
  return (
    <div>
      {source.original_url && (
        <a
          href={source.original_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 mb-4 px-4 py-3 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors"
        >
          <Globe className="w-4 h-4 shrink-0" />
          <span className="text-sm font-medium truncate">{source.original_url}</span>
          <ExternalLink className="w-3.5 h-3.5 shrink-0 ml-auto" />
        </a>
      )}
      <PassageCard source={source} className="text-sm text-foreground leading-[1.7] whitespace-pre-wrap">
        {cleanPassageText(source.document)}
      </PassageCard>
    </div>
  )
}

export function ImageViewer({
  source,
  embedUrl,
  embedStatus,
  onRefresh,
  isRefreshing,
}: {
  source: DisplaySource
  embedUrl: string | null
  embedStatus: 'ready' | 'loading' | 'error'
  onRefresh?: () => void
  isRefreshing?: boolean
}) {
  if (source.isExpired || embedStatus === 'error' || (embedStatus === 'ready' && !embedUrl)) {
    return (
      <UnavailablePreview
        source={source}
        message="Image preview unavailable"
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
      />
    )
  }

  if (embedStatus === 'loading') {
    return <Skeleton className="h-64 w-full max-w-md mx-auto rounded-lg" />
  }

  return (
    <div className="flex justify-center h-full min-h-0">
      <img
        src={embedUrl ?? undefined}
        alt={source.title}
        className="max-w-full max-h-full rounded-lg object-contain"
      />
    </div>
  )
}
